import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  getBaseRequestGlobalData,
  isSuccessfulResponse,
} from 'src/app/shared/utils/evergentUtils';
import { SessionService } from '../../../services/session.service';
import { RefreshTokenRequestMessage } from './model/refresh-token/refresh-token.request.message';
import { RefreshTokenRequestModel } from './model/refresh-token/refresh-token.request.model';
import { RefreshTokenResponseMessage } from './model/refresh-token/refresh-token.response.message';
import { AppConfig } from '../../../utils/constants/app-config.constant';
import { KEYS } from '../../../utils/constants/storage.constants';
import { LocalStorageService } from '../../storage/local-storage.service';
import { saveTokenExpiry } from 'src/app/shared/utils/LoginUtils';
import { getApiUrl } from 'src/app/shared/utils/apiUtils';
import { EntitlementService } from '../entitlement/entitlement.service';

/**
 * Service responsible for handling token refresh operations.
 * Manages the refresh token lifecycle and authentication state.
 *
 * @class RefreshService
 */
@Injectable({
  providedIn: 'root',
})
export class RefreshService {
  /**
   * Initializes the service with required dependencies
   * @param session - Service for managing user sessions
   * @param http - HTTP client for making API requests
   * @param localStorage - Service for managing local storage
   */
  constructor(
    private readonly session: SessionService,
    private readonly http: HttpClient,
    private readonly localStorage: LocalStorageService,
    private readonly entitlementService: EntitlementService
  ) {}

  /**
   * Makes a refresh token call to the server and updates the evergent tokens
   * Also fetches fresh entitlements (xAuthToken) after token refresh succeeds
   * @param appConfig - Application configuration
   * @returns Promise<void> - Promise that resolves when both refresh and entitlements are complete
   */
  public async makeRefreshTokenCall(appConfig: AppConfig): Promise<void> {
    try {
      const url = getApiUrl(appConfig.UmsURL, 'REFRESH_TOKEN');
      const tokenRequestMessage = this.getRefreshTokenRequestMessage(appConfig);
      const headers = this.getHeaders();

      const refreshTokenResponseMessage = await this.http
        .post<RefreshTokenResponseMessage>(url, tokenRequestMessage, {
          headers,
        })
        .pipe(catchError(this.handleError))
        .toPromise();

      this.handleRefreshTokenResponse(refreshTokenResponseMessage);

      // After access token refresh succeeds, get fresh xAuthToken
      const selectedProfileId = this.localStorage.getItem(KEYS.SELECTED_PROFILE_ID) || '';
      if (selectedProfileId) {
        try {
          const entitlementResult = await this.entitlementService
            .getEntitlement(selectedProfileId)
            .toPromise();

          this.entitlementService.handleEntitlementResult(entitlementResult);
        } catch (entitlementError) {
          console.error('Error fetching entitlements after token refresh:', entitlementError);
          // Don't fail the refresh if entitlements fail, but log it
        }
      }
    } catch (error) {
      console.error('Error refreshing token:', error);
      throw error;
    }
  }

  /**
   * Creates a refresh token request message
   * @param appConfig - Application configuration
   * @returns RefreshTokenRequestMessage - The formatted request message
   */
  private getRefreshTokenRequestMessage(
    appConfig: AppConfig
  ): RefreshTokenRequestMessage {
    const requestMessage = new RefreshTokenRequestMessage();
    const requestContent: RefreshTokenRequestModel = {
      ...new RefreshTokenRequestModel(),
      refreshToken: this.localStorage.getItem(KEYS.REFRESH_TOKEN) || '',
      ...getBaseRequestGlobalData(appConfig),
    };

    requestMessage.RefreshTokenRequestMessage = requestContent;
    return requestMessage;
  }

  /**
   * Handles the refresh token response
   * Updates authentication tokens and session state
   * @param result - The refresh token response
   */
  private handleRefreshTokenResponse(
    result: RefreshTokenResponseMessage
  ): void {
    this.localStorage.setItem(
      KEYS.REFRESH_TOKEN_RESPONSE,
      JSON.stringify(result)
    );
    if (
      result.RefreshTokenResponseMessage &&
      isSuccessfulResponse(result.RefreshTokenResponseMessage)
    ) {
      this.updateEvergentTokens(result.RefreshTokenResponseMessage);
    } else {
      this.clearAuthToken();
      const errorMsg =
        result.RefreshTokenResponseMessage?.failureMessage?.[0]?.errorMessage ||
        'Refresh Token Failed';
      throw new Error(errorMsg);
    }
  }

  /**
   * Updates authentication tokens
   * @param response - The successful refresh token response
   */
  private updateEvergentTokens(response: any): void {
    this.localStorage.setItem(KEYS.EVERGENT_ACCESS_TOKEN, response.accessToken);
    saveTokenExpiry(response.accessToken);
    this.session.setSessionID(response.accessToken);
    this.localStorage.setItem(KEYS.REFRESH_TOKEN, response.refreshToken);
  }

  /**
   * Clears the authentication token
   */
  private clearAuthToken(): void {
    this.localStorage.removeItem(KEYS.EVERGENT_ACCESS_TOKEN);
    this.localStorage.removeItem(KEYS.REFRESH_TOKEN);
    this.localStorage.removeItem(KEYS.EVERGENT_ACCESS_TOKEN_EXPIRY);
    this.localStorage.setItem(KEYS.EVERGENT_OVAT_TOKEN, '');
  }

  /**
   * Creates HTTP headers for the request
   * @returns HttpHeaders - Headers for the API request
   */
  private getHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-type': 'application/json',
    });
  }

  /**
   * Handles HTTP errors
   * @param error - The HTTP error response
   * @returns Observable<never> - Error observable
   */
  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMessage = 'An error occurred';
    if (error.error instanceof ErrorEvent) {
      errorMessage = `Error: ${error.error.message}`;
    } else {
      errorMessage = `Backend returned code ${error.status}, body was: ${error.error}`;
    }
    console.error(errorMessage);
    return throwError(() => new Error(errorMessage));
  }
}
